import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { isCampingGroundPublicData } from '../../util/data';

import ListingSection from './ListingSection';
import ListingSectionItem from './ListingSectionItem';
import SectionCampingPeriodsMaybe from './SectionCampingPeriodsMaybe';

export const SectionMoreInformationMaybeLoadingSkeleton = () => (
  <ListingSection>
    <ListingSectionItem>
      <Skeleton height="160px" sx={{ my: 2, borderRadius: 2 }} variant="rounded" width="100%" />
    </ListingSectionItem>
  </ListingSection>
);

const SectionMoreInformationMaybe = ({
  publicData,
  bookingLimitOptions,
  amountOfSeats,
  translations,
}) => {
  const intl = useIntl();

  if (!publicData) {
    return null;
  }

  const bookingLimit = publicData.bookingLimit;
  let bookingLimitOption;
  if (!Array.isArray(bookingLimit)) {
    bookingLimitOption = bookingLimitOptions.find((option) => option.key === bookingLimit);
  } else {
    bookingLimitOption = bookingLimitOptions.find(
      (option) => option.key === bookingLimit[bookingLimit.length - 1],
    );
  }
  const bookingMinimum = publicData.bookingMinimum || 'min_1';

  // Determine allWheelDriveOption more concisely
  const allWheelDriveNeeded = publicData.allWheelDrive === 'yes';

  const availableSeatsTextLabel = intl.formatMessage(
    {
      id: 'ListingPage.availableSeats.text',
    },
    { seats: amountOfSeats },
  );
  const availableSeatsLabel = intl.formatMessage({ id: 'ListingPage.availableSeats' });
  const combinedLabel = `${availableSeatsTextLabel} ${availableSeatsLabel}`;

  const bookingMinimumLabel = intl.formatMessage({ id: `Filter.bookingMinimum.${bookingMinimum}` });
  const minimumNightsLabel = (bookingMinimumLabel).charAt(0).toLowerCase() + bookingMinimumLabel.slice(1);

  // Recurring closing periods and seasonal prices only exist on camping grounds.
  const isCampingGround = isCampingGroundPublicData(publicData);
  const closedPeriods = isCampingGround ? publicData.closedPeriods : null;
  const seasonalPrices = isCampingGround ? publicData.seasonalPrices : null;
  const hasCampingPeriods = (closedPeriods?.length || 0) + (seasonalPrices?.length || 0) > 0;

  return bookingLimitOption || allWheelDriveNeeded || hasCampingPeriods ? (
    <Grid container spacing={2}>
      <ListingSectionItem params={{ size: { lg: 12 } }}>
        <Stack width="100%">
          <Typography
            component="h3"
            mb={2}
            sx={{ textAlign: { xs: 'center', md: 'left' } }}
            variant="h4"
          >
            <FormattedMessage id="ListingPage.moreInformationTitle" />
          </Typography>
          <Box>
            <Stack>
              <Box>
                <Chip
                  icon={<InfoOutlinedIcon />}
                  label={<Typography variant="body2">{combinedLabel}</Typography>}
                  sx={{
                    '&.MuiChip-root': { height: 'auto' },
                  }}
                  variant="info"
                />
              </Box>
              {bookingLimitOption && bookingLimitOption.key !== 'limit_bigger_4' ? (
                <Box>
                  <Chip
                    icon={<InfoOutlinedIcon />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          <FormattedMessage id={bookingLimitOption.label} />
                          {' '}
                          <FormattedMessage id="ListingPage.maxNightsInfo" />
                        </Typography>
                      </Box>
                    }
                    sx={{
                      '&.MuiChip-root': { height: 'auto' },
                    }}
                    variant="info"
                  />
                </Box>
              ) : null}
              <Box>
                <Chip
                  icon={<InfoOutlinedIcon />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        <FormattedMessage id="ListingPage.minNightsInfo" values={{
                          nights: minimumNightsLabel,
                        }} />
                      </Typography>
                    </Box>
                  }
                  sx={{
                    '&.MuiChip-root': { height: 'auto' },
                  }}
                  variant="info"
                />
              </Box>
              {allWheelDriveNeeded ? (
                <Box>
                  <Chip
                    icon={<InfoOutlinedIcon />}
                    label={
                      <Typography variant="body2">
                        <FormattedMessage id="ListingPage.allWheelDriveInfo" />
                      </Typography>
                    }
                    sx={{
                      '&.MuiChip-root': { height: 'auto' },
                    }}
                    variant="info"
                  />
                </Box>
              ) : null}
            </Stack>
            <SectionCampingPeriodsMaybe
              closedPeriods={closedPeriods}
              seasonalPrices={seasonalPrices}
              translations={translations}
            />
          </Box>
        </Stack>
      </ListingSectionItem>
    </Grid>
  ) : null;
};

export default SectionMoreInformationMaybe;
